import type { Entity, FeedItem } from './types';
import { SUB_ROUTE_UPDATES } from '../../redux/constants';
import {
  toCurrencyTruncated,
  toCurrency,
  toPercentage,
} from '@/utilities/formatting';
import { format, isSameDay } from 'date-fns';
import type {
  Company,
  Event,
  Article,
  ExternalArticle,
  Brief,
  Narrative,
} from '@simplywallst/services/src/endpoints/getDashboardFeed';

const formatCurrency = (amount = 0, currencyISO?: string) => {
  if (amount > 99999) {
    return toCurrencyTruncated(amount, 1, currencyISO);
  }
  return toCurrency(amount, 2, currencyISO);
};

const formatterPercentage = (value: number | null) =>
  value !== null ? toPercentage(value, 1) : 'n/a';

export const getDefault = (): Entity => ({
  companyId: '',
  name: '',
  canonicalUrl: '',
  tickerSymbol: '',
  uniqueSymbol: '',
  sharePriceFormatted: '',
  return7D: null,
  return7DFormatted: '',
  return1YrAbs: null,
  return1YrAbsFormatted: '',
  scores: [0, 0, 0, 0, 0],
  feedItems: [],
});

const getEvent = (
  {
    event_id,
    date,
    headline,
    situation,
    key_dev_type,
    activity_id: activityId = '',
    categories,
  }: Event,
  eventSubRoute?: string
): FeedItem => {
  const id = event_id.toString();
  return {
    id: id,
    date,
    dateFormatted: format(date || 0, 'MMM dd'),
    title: headline,
    text: situation,
    type: 'event',
    subType: key_dev_type || '',
    url:
      typeof eventSubRoute !== 'undefined'
        ? `${eventSubRoute}${id}`
        : `/dashboard/${SUB_ROUTE_UPDATES}/event/${id}`,
    activityId,
    categories,
  };
};

const getArticle = ({
  date,
  title,
  excerpt,
  url,
  thumbnail,
  topic,
  activity_id = '',
  categories,
}: Article): FeedItem => {
  return {
    id: url,
    date,
    dateFormatted: format(date || 0, 'MMM dd'),
    title,
    text: excerpt,
    type: 'article',
    subType: topic,
    url,
    thumbnail,
    activityId: activity_id,
    categories,
  };
};

const getExternalArticle = ({
  guid,
  date,
  title,
  content,
  link,
  activity_id = '',
  virtual,
  categories,
}: ExternalArticle): FeedItem => {
  return {
    id: guid,
    date,
    dateFormatted: format(date || 0, 'MMM dd'),
    title,
    text: content,
    type: 'external-article',
    subType: '',
    url: link,
    thumbnail: '',
    activityId: activity_id,
    virtual,
    categories,
  };
};

export const getBrief = (
  {
    name,
    date,
    title,
    description: text,
    outcome,
    virtual,
    activity_id = '',
    categories,
  }: Brief,
  canonicalUrl: string
): FeedItem => {
  const id = name || title.replace(/ /g, '');
  const key = JSON.stringify([id, date, outcome]);
  return {
    id: key,
    date,
    dateFormatted: format(date || 0, 'MMM dd'),
    title,
    text: text,
    url: canonicalUrl,
    type: 'brief',
    subType: name,
    outcome,
    virtual,
    activityId: activity_id,
    briefName: name,
    categories,
  };
};

export const getNarrative = ({
  activity_id = '',
  author_image_url,
  categories,
  content,
  date,
  title,
  type,
  url,
  uuid,
}: Narrative): FeedItem => {
  return {
    activityId: activity_id,
    authorImageUrl: author_image_url,
    categories,
    date,
    dateFormatted: format(date || 0, 'MMM dd'),
    id: uuid,
    subType: 'Narrative',
    text: content,
    title,
    type,
    url,
  };
};

export const getCompanyFeedItems = (
  events: (Event | Article | ExternalArticle | Brief | Narrative)[],
  {
    canonicalUrl,
    eventSubRoute,
    types = ['article', 'brief', 'external-article', 'event', 'narrative'],
  }: {
    canonicalUrl: string;
    eventSubRoute?: string;
    // This is only client side. Ideally the API can filter the events.
    types?: (
      | 'article'
      | 'external-article'
      | 'brief'
      | 'event'
      | 'narrative'
    )[];
  }
) => {
  return events
    .filter((n) => {
      return types.includes(n.type);
    })
    .reduce((a, b) => {
      switch (b.type) {
        case 'event':
          return [...a, getEvent(b, eventSubRoute)];
        case 'brief':
          return [...a, getBrief(b, canonicalUrl)];
        case 'article':
          return [...a, getArticle(b)];
        case 'external-article':
          return [...a, getExternalArticle(b)];
        case 'narrative':
          return [...a, getNarrative(b)];
        default:
          return a;
      }
    }, [] as FeedItem[])
    .reduce((a, b) => {
      const { type } = b;
      const prevItem = a[a.length - 1];
      if (
        type === 'event' &&
        typeof prevItem !== 'undefined' &&
        (prevItem.type === 'event' || prevItem.type === 'multi-event')
      ) {
        const date = b.date;
        const prevItemDate = prevItem.date;
        if (
          date !== null &&
          prevItemDate !== null &&
          isSameDay(date, prevItemDate)
        ) {
          const safeId = `${prevItem.id},${b.id}`;
          // Replace previous reduced entry
          a.splice(a.length - 1, 1, {
            ...prevItem,
            id: safeId,
            type: 'multi-event',
          });
          return a;
        }
      }
      return [...a, b];
    }, [] as FeedItem[]);
};

export function selector(data: Company[] = []): {
  keys: string[];
  entities: Record<string, Entity>;
} {
  const feed: Entity[] = data
    .map((n) => {
      const feedItems: FeedItem[] = getCompanyFeedItems(n.events.data, {
        canonicalUrl: n.canonical_url,
      });
      return {
        companyId: n.id,
        uniqueSymbol: n.unique_symbol,
        name: n.name,
        canonicalUrl: n.canonical_url,
        tickerSymbol: n.ticker_symbol,
        sharePriceFormatted: formatCurrency(n.last_share_price, n.currency_iso),
        return7D: n.return_7d,
        return7DFormatted: formatterPercentage(n.return_7d),
        return1YrAbs: n.return_1yr_abs,
        return1YrAbsFormatted: formatterPercentage(n.return_1yr_abs),
        scores: n.snowflake.data.axes,
        feedItems,
      };
    })
    .filter((n) => n.feedItems.length > 0);
  return {
    entities: feed.reduce(
      (a, b) => ({
        ...a,
        [b.companyId]: b,
      }),
      {}
    ),
    keys: feed.map((n) => n.companyId),
  };
}
